import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2, Settings, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TaxConfigPanel = ({ clientId }) => {
  const [configs, setConfigs] = useState([]);
  const [definitions, setDefinitions] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [clientId]);

  const loadData = async () => {
    try {
      const [configsRes, defsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/clients/${clientId}/tax-config`),
        axios.get(`${BACKEND_URL}/api/tax-config/definitions`),
      ]);
      setConfigs(configsRes.data);
      setDefinitions(defsRes.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (configId) => {
    if (!window.confirm('¿Eliminar esta configuración de impuesto?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/tax-config/${configId}`);
      loadData();
    } catch (error) {
      alert('Error al eliminar');
    }
  };

  const handleEdit = (config) => {
    setEditingConfig(config);
    setShowModal(true);
  };

  const handleCreate = () => {
    setEditingConfig(null);
    setShowModal(true);
  };

  const getPeriodicidadBadge = (config) => {
    const p = config.periodicidad;
    if (!p) return null;
    const colors = {
      Mensual: 'bg-blue-100 text-blue-800',
      Bimestral: 'bg-indigo-100 text-indigo-800',
      Cuatrimestral: 'bg-violet-100 text-violet-800',
      Anual: 'bg-emerald-100 text-emerald-800',
    };
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[p] || 'bg-slate-100 text-slate-800'}`}>{p}</span>;
  };

  // Find which taxes are already configured
  const configuredTaxes = configs.map(c => {
    if (c.municipio) return `${c.tipo_impuesto}__${c.municipio}`;
    return c.tipo_impuesto;
  });

  if (loading) {
    return <div className="text-center py-8 text-slate-500">Cargando configuraciones...</div>;
  }

  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6" data-testid="tax-config-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl tracking-tight font-semibold" style={{ fontFamily: 'Outfit' }}>
            Configuración de Impuestos
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            {configs.length} impuesto{configs.length !== 1 ? 's' : ''} configurado{configs.length !== 1 ? 's' : ''}
          </p>
        </div>
        <Button onClick={handleCreate} className="bg-[#1F3864] hover:bg-[#172b4d] text-white" data-testid="add-tax-config-btn">
          <Plus className="w-4 h-4 mr-2" />
          Agregar Impuesto
        </Button>
      </div>

      {configs.length === 0 ? (
        <div className="text-center py-10 text-slate-400">
          <Settings className="w-10 h-10 mx-auto mb-3 text-slate-300" />
          <p className="text-sm">No hay impuestos configurados</p>
          <p className="text-xs mt-1">Agrega los impuestos que aplican a este cliente</p>
        </div>
      ) : (
        <div className="space-y-2">
          {configs.map((config) => (
            <div
              key={config._id}
              className="flex items-center justify-between px-4 py-3 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
              data-testid={`tax-config-item-${config._id}`}
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="w-8 h-8 rounded-full bg-[#1F3864] flex items-center justify-center flex-shrink-0">
                  <Check className="w-4 h-4 text-white" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">{config.tipo_impuesto}</p>
                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                    {getPeriodicidadBadge(config)}
                    {config.municipio && (
                      <span className="px-2 py-0.5 bg-teal-100 text-teal-800 rounded-full text-xs font-medium">{config.municipio}</span>
                    )}
                    {config.tipo_persona_renta && (
                      <span className="px-2 py-0.5 bg-slate-100 text-slate-700 rounded-full text-xs font-medium">P. {config.tipo_persona_renta}</span>
                    )}
                    {config.anio && (
                      <span className="px-2 py-0.5 bg-slate-100 text-slate-700 rounded-full text-xs font-medium">Año {config.anio}</span>
                    )}
                    {!config.activo && (
                      <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-medium">Inactivo</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <Button variant="ghost" size="sm" onClick={() => handleEdit(config)} data-testid={`edit-tax-${config._id}`}>
                  <Edit className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => handleDelete(config._id)} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`delete-tax-${config._id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingConfig ? 'Editar Configuración' : 'Agregar Impuesto'}</DialogTitle>
          </DialogHeader>
          <TaxConfigForm
            clientId={clientId}
            config={editingConfig}
            definitions={definitions}
            configuredTaxes={configuredTaxes}
            onSuccess={() => { setShowModal(false); loadData(); }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

const TaxConfigForm = ({ clientId, config, definitions, configuredTaxes, onSuccess }) => {
  const [selectedTipo, setSelectedTipo] = useState(config?.tipo_impuesto || '');
  const [formData, setFormData] = useState({
    periodicidad: config?.periodicidad || '',
    municipio: config?.municipio || '',
    tipo_persona_renta: config?.tipo_persona_renta || '',
    anio: config?.anio || new Date().getFullYear() - 1,
    notas: config?.notas || '',
    activo: config?.activo !== undefined ? config.activo : true,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const currentDef = definitions.find(d => d.nombre === selectedTipo);
  const campos = currentDef?.campos || [];

  const handleTipoChange = (tipo) => {
    setSelectedTipo(tipo);
    const def = definitions.find(d => d.nombre === tipo);
    setFormData(prev => ({
      ...prev,
      periodicidad: def?.periodicidad_default || '',
      municipio: '',
      tipo_persona_renta: '',
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    // Validations
    if (!selectedTipo) { setError('Seleccione un tipo de impuesto'); setSubmitting(false); return; }
    if (campos.includes('municipio') && !formData.municipio) { setError('Seleccione un municipio'); setSubmitting(false); return; }
    if (campos.includes('periodicidad') && !formData.periodicidad) { setError('Seleccione la periodicidad'); setSubmitting(false); return; }
    if (campos.includes('tipo_persona_renta') && !formData.tipo_persona_renta) { setError('Seleccione tipo de persona'); setSubmitting(false); return; }

    const payload = {
      client_id: clientId,
      tipo_impuesto: selectedTipo,
      periodicidad: formData.periodicidad || (currentDef?.periodicidad_default || null),
      municipio: formData.municipio || null,
      tipo_persona_renta: formData.tipo_persona_renta || null,
      anio: campos.includes('anio') ? parseInt(formData.anio) : null,
      notas: formData.notas || null,
      activo: formData.activo,
    };

    try {
      if (config?._id) {
        await axios.put(`${BACKEND_URL}/api/tax-config/${config._id}`, payload);
      } else {
        await axios.post(`${BACKEND_URL}/api/clients/${clientId}/tax-config`, payload);
      }
      onSuccess();
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSubmitting(false);
    }
  };

  // Available taxes (not already configured unless editing)
  const availableTaxes = definitions.filter(d => {
    if (config && d.nombre === config.tipo_impuesto) return true;
    // For ICA/Exógena municipal, allow same type with different municipio
    if (d.campos?.includes('municipio')) return true;
    return !configuredTaxes.includes(d.nombre);
  });

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="tax-config-form">
      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3">{error}</div>
      )}

      {/* Tipo de Impuesto */}
      <div>
        <Label>Tipo de Impuesto *</Label>
        <select
          value={selectedTipo}
          onChange={(e) => handleTipoChange(e.target.value)}
          className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
          required
          disabled={!!config}
          data-testid="tax-type-select"
        >
          <option value="">Seleccionar impuesto...</option>
          {availableTaxes.map(d => (
            <option key={d.id} value={d.nombre}>{d.nombre}</option>
          ))}
        </select>
      </div>

      {/* Dynamic Fields */}
      {selectedTipo && (
        <div className="space-y-4 border-t pt-4">
          {/* Periodicidad */}
          {campos.includes('periodicidad') && currentDef?.periodicidades && (
            <div>
              <Label>Periodicidad *</Label>
              <div className="flex gap-2 mt-1">
                {currentDef.periodicidades.map(p => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, periodicidad: p }))}
                    className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                      formData.periodicidad === p
                        ? 'bg-[#1F3864] text-white border-[#1F3864]'
                        : 'bg-white text-slate-700 border-slate-300 hover:border-[#1F3864]'
                    }`}
                    data-testid={`periodicidad-${p}`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Periodicidad por defecto (solo lectura) */}
          {!campos.includes('periodicidad') && currentDef?.periodicidad_default && (
            <div>
              <Label>Periodicidad</Label>
              <div className="mt-1">
                <span className="px-3 py-1.5 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium">
                  {currentDef.periodicidad_default} (por defecto)
                </span>
              </div>
            </div>
          )}

          {/* Municipio */}
          {campos.includes('municipio') && currentDef?.municipios && (
            <div>
              <Label>Municipio *</Label>
              <select
                value={formData.municipio}
                onChange={(e) => setFormData(prev => ({ ...prev, municipio: e.target.value }))}
                className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
                required
                data-testid="municipio-select"
              >
                <option value="">Seleccionar municipio...</option>
                {currentDef.municipios.map(m => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>
          )}

          {/* Tipo Persona Renta */}
          {campos.includes('tipo_persona_renta') && (
            <div>
              <Label>Tipo de Persona *</Label>
              <div className="flex gap-2 mt-1">
                {['Jurídica', 'Natural'].map(t => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, tipo_persona_renta: t }))}
                    className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                      formData.tipo_persona_renta === t
                        ? 'bg-[#1F3864] text-white border-[#1F3864]'
                        : 'bg-white text-slate-700 border-slate-300 hover:border-[#1F3864]'
                    }`}
                    data-testid={`tipo-persona-${t}`}
                  >
                    Persona {t}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Año */}
          {campos.includes('anio') && (
            <div>
              <Label>Año Gravable *</Label>
              <select
                value={formData.anio}
                onChange={(e) => setFormData(prev => ({ ...prev, anio: parseInt(e.target.value) }))}
                className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
                data-testid="anio-select"
              >
                {[2023, 2024, 2025, 2026].map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
          )}

          {/* Notas */}
          <div>
            <Label>Notas (opcional)</Label>
            <textarea
              value={formData.notas}
              onChange={(e) => setFormData(prev => ({ ...prev, notas: e.target.value }))}
              className="flex min-h-[60px] w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
              placeholder="Observaciones..."
            />
          </div>

          {/* Activo toggle */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.activo}
              onChange={(e) => setFormData(prev => ({ ...prev, activo: e.target.checked }))}
              className="w-4 h-4"
            />
            <span className="text-sm text-slate-700">Configuración activa</span>
          </label>
        </div>
      )}

      <div className="flex justify-end pt-2 border-t">
        <Button type="submit" disabled={submitting} className="bg-[#1F3864] hover:bg-[#172b4d] text-white" data-testid="save-tax-config-btn">
          {submitting ? 'Guardando...' : config ? 'Actualizar' : 'Guardar Configuración'}
        </Button>
      </div>
    </form>
  );
};

export default TaxConfigPanel;
